import React, { useState } from "react";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  LineChart,
  Line,
} from "recharts";
import { panelHeading } from "../../styles";

interface RankPoint {
  gwNumber: number;
  gwName: string;
  gwShortName: string;
  rank: number;
  score: number;
}

interface Props {
  rankHistory: RankPoint[];
  memberCount: number;
  currentRank: string;
  combined?: boolean; // show cumulative/per-gw tab toggle
}

const panelStyle: React.CSSProperties = {
  background: "var(--color-surface)",
  border: "1px solid var(--color-border-subtle)",
  borderRadius: 12,
  padding: "1rem 1.25rem",
  marginBottom: "1rem",
};

const tooltipStyle = {
  background: "var(--color-surface)",
  border: "1px solid var(--color-border)",
  borderRadius: 8,
  fontSize: "0.78rem",
};

export default function TrajectoryPanel({ rankHistory, combined }: Props) {
  const [chartTab, setChartTab] = useState<"cumulative" | "per-gw">("cumulative");

  if (rankHistory.length === 0) {
    return (
      <div style={panelStyle}>
        <h2 style={panelHeading}>Score Progression</h2>
        <p style={{ margin: "0.5rem 0 0", fontSize: "0.85rem", color: "var(--color-text-dim)", fontStyle: "italic" }}>
          No data yet — check back after the first gameweek completes.
        </p>
      </div>
    );
  }

  if (combined) {
    const cumulativeData = [
      { name: "Start", score: 0 },
      ...rankHistory.map((p) => ({ name: p.gwShortName || p.gwName, score: p.score })),
    ];

    const perGwData = rankHistory.map((p, i) => ({
      name: p.gwShortName || p.gwName,
      score: i === 0 ? p.score : p.score - rankHistory[i - 1].score,
    }));

    const tickInterval = rankHistory.length > 8 ? Math.floor(rankHistory.length / 5) : 0;

    return (
      <div style={panelStyle}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.75rem" }}>
          <h2 style={{ ...panelHeading, margin: 0 }}>Score Progression</h2>
          <div style={{ display: "flex", gap: "0.25rem" }}>
            {(["cumulative", "per-gw"] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setChartTab(tab)}
                style={{
                  padding: "0.2rem 0.6rem",
                  fontSize: "0.75rem",
                  borderRadius: 6,
                  border: "none",
                  cursor: "pointer",
                  fontFamily: "var(--font-body)",
                  background: chartTab === tab ? "var(--color-accent)" : "var(--color-surface-alt)",
                  color: chartTab === tab ? "var(--color-btn-primary-text)" : "var(--color-text-muted)",
                }}
              >
                {tab === "cumulative" ? "Cumulative" : "Per GW"}
              </button>
            ))}
          </div>
        </div>

        {chartTab === "cumulative" ? (
          <ResponsiveContainer width="100%" height={140}>
            <AreaChart data={cumulativeData} margin={{ top: 4, right: 4, bottom: 4, left: 0 }}>
              <defs>
                <linearGradient id="traj-grad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--color-accent)" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="var(--color-accent)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-subtle)" />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 9, fill: "var(--color-text-dim)" }}
                axisLine={false}
                tickLine={false}
                interval={tickInterval}
              />
              <YAxis tick={{ fontSize: 9, fill: "var(--color-text-dim)" }} axisLine={false} tickLine={false} domain={[0, "auto"]} />
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={(value: any) => [`${value} pts`, "Total"]}
              />
              <Area
                type="monotone"
                dataKey="score"
                stroke="var(--color-accent)"
                strokeWidth={2}
                fill="url(#traj-grad)"
                dot={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <ResponsiveContainer width="100%" height={140}>
            <BarChart data={perGwData} margin={{ top: 4, right: 4, bottom: 4, left: 0 }}>
              <defs>
                <linearGradient id="traj-bar-grad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--color-accent)" stopOpacity={0.9} />
                  <stop offset="95%" stopColor="var(--color-accent)" stopOpacity={0.5} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-subtle)" vertical={false} />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 9, fill: "var(--color-text-dim)" }}
                axisLine={false}
                tickLine={false}
                interval={tickInterval}
              />
              <YAxis tick={{ fontSize: 9, fill: "var(--color-text-dim)" }} axisLine={false} tickLine={false} domain={[0, "auto"]} />
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={(value: any) => [`${value} pts`, "This round"]}
              />
              <Bar dataKey="score" fill="url(#traj-bar-grad)" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    );
  }

  // Non-combined: original single-line trajectory (used by LeaguePage)
  const chartData = [
    { name: "Start", score: 0 },
    ...rankHistory.map((p) => ({ name: p.gwShortName || p.gwName || `GW${p.gwNumber}`, score: p.score })),
  ];

  return (
    <div style={panelStyle}>
      <h2 style={{ ...panelHeading, marginBottom: "0.75rem" }}>Your Trajectory</h2>
      <ResponsiveContainer width="100%" height={90}>
        <LineChart data={chartData} margin={{ top: 4, right: 4, bottom: 4, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-subtle)" />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 9, fill: "var(--color-text-dim)" }}
            axisLine={false}
            tickLine={false}
            interval={rankHistory.length > 10 ? Math.floor(rankHistory.length / 5) : 0}
          />
          <YAxis hide />
          <Tooltip
            formatter={(value: any) => [`${value} pts`, "Score"]}
            labelStyle={{ fontSize: "0.75rem" }}
            contentStyle={tooltipStyle}
          />
          <Line
            type="monotone"
            dataKey="score"
            stroke="var(--color-accent)"
            strokeWidth={2}
            dot={rankHistory.length <= 8 ? { r: 3, fill: "var(--color-accent)" } : false}
            activeDot={{ r: 4 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
